import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import type { Engagement } from '@ai-secure-twin/shared';
import { api, ApiError } from '../../lib/api';
import { NewEngagementDialog } from './NewEngagementDialog';
import { StageBadge } from './StageStatus';

/** Stages an engagement has finished with; everything else is still in flight. */
const CLOSED = new Set(['PUBLISHED', 'CANCELLED', 'FAILED']);
const AWAITING_APPROVAL = new Set([
  'SCOPE_PENDING_APPROVAL',
  'PLAN_PENDING_APPROVAL',
  'REPORT_PENDING_APPROVAL',
]);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

export function EngagementsPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [created, setCreated] = useState<string | null>(null);

  const engagements = useQuery({ queryKey: ['engagements'], queryFn: api.listEngagements });
  const applications = useQuery({ queryKey: ['applications'], queryFn: api.listApplications });

  const items: Engagement[] = engagements.data?.items ?? [];
  const active = items.filter((e) => !CLOSED.has(e.stage));
  const awaiting = items.filter((e) => AWAITING_APPROVAL.has(e.stage));
  const scoped = items.filter((e) => e.currentScopeVersion !== undefined);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <span className="eyebrow">Workspace</span>
          <h1>Engagements</h1>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setDialogOpen(true)}>
          New engagement
        </button>
      </div>

      {created ? (
        <p className="banner-ok" role="status">
          Engagement created in DRAFT.{' '}
          <Link to={`/app/engagements/${created}`} className="table-link">
            Open it
          </Link>{' '}
          to define its scope.
        </p>
      ) : null}

      <div className="kpis">
        <div className="kpi">
          <span>Active engagements</span>
          <span>{engagements.isLoading ? '—' : active.length}</span>
        </div>
        <div className="kpi">
          <span>Awaiting approval</span>
          <span style={{ color: awaiting.length > 0 ? 'var(--sev-high)' : undefined }}>
            {engagements.isLoading ? '—' : awaiting.length}
          </span>
        </div>
        <div className="kpi">
          <span>With an approved scope</span>
          <span>{engagements.isLoading ? '—' : scoped.length}</span>
        </div>
        <div className="kpi">
          <span>Applications registered</span>
          <span>{applications.isLoading ? '—' : (applications.data?.items.length ?? 0)}</span>
        </div>
      </div>

      <section className="card panel table-wrap" aria-labelledby="eng-table">
        <h2 id="eng-table" className="panel-title">
          All engagements
        </h2>

        {engagements.isLoading ? (
          <p className="muted">Loading engagements…</p>
        ) : engagements.isError ? (
          <div role="alert">
            <p className="login-error">
              {engagements.error instanceof ApiError
                ? engagements.error.message
                : 'Could not load engagements.'}
            </p>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => void engagements.refetch()}
            >
              Try again
            </button>
          </div>
        ) : items.length === 0 ? (
          /* A real empty state: this tenant genuinely has none yet, which is not an error. */
          <div className="empty">
            <p>No engagements yet.</p>
            <p className="muted">
              An engagement is a test campaign against one application in one environment. Create
              one to begin.
            </p>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setDialogOpen(true)}
            >
              New engagement
            </button>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Engagement</th>
                <th>Environment</th>
                <th>Stage</th>
                <th>Scope</th>
                <th>Created</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id}>
                  <td>
                    <Link to={`/app/engagements/${e.id}`} className="table-link">
                      {e.name}
                    </Link>
                    <div className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                      {e.id}
                    </div>
                  </td>
                  <td className="muted">{e.environment}</td>
                  <td>
                    <StageBadge stage={e.stage} />
                  </td>
                  <td className="muted">
                    {e.currentScopeVersion === undefined
                      ? 'Not defined'
                      : `v${e.currentScopeVersion}`}
                  </td>
                  <td className="muted">{formatDate(e.createdAt)}</td>
                  <td className="muted">{formatDate(e.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {dialogOpen ? (
        <NewEngagementDialog
          onClose={() => setDialogOpen(false)}
          onCreated={(id) => {
            setCreated(id);
            setDialogOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
